import { BaseComponent } from '../core/BaseComponent.js';
import { apiClient } from '../utils/ApiClient.js';

/**
 * FormRepeater - Handles dynamic repeatable form sections
 */
export class FormRepeater extends BaseComponent {
    constructor(selector, options = {}) {
        super(selector, options);
        
        this.repeaterSelector = options.repeaterSelector || '.form-section[data-repeater-id]';
        this.addButtonSelector = options.addButtonSelector || '.add-repeater-item';
        this.removeButtonSelector = options.removeButtonSelector || '.remove-repeater-item';
    }

    init() {
        if (!super.init()) return this;
        
        this.setupEventListeners();
        this.setupFormSubmit();
        
        return this;
    }

    setupEventListeners() {
        // Use event delegation for dynamically added buttons
        document.addEventListener('click', (e) => {
            if (e.target.closest(this.addButtonSelector)) {
                e.preventDefault();
                this.addRepeaterItem();
            }

            if (e.target.closest(this.removeButtonSelector)) {
                e.preventDefault();
                this.removeRepeaterItem(e.target);
            }
        });
    }

    setupFormSubmit() {
        this.element.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    /**
     * Add new repeater item
     */
    addRepeaterItem() {
        const items = this.getRepeaterItems();
        if (items.length === 0) return;

        const firstItem = items[0];
        const lastItem = items[items.length - 1];
        const newIndex = items.length;

        // Clone first item
        const clone = firstItem.cloneNode(true);

        // Clear all inputs
        this.clearInputs(clone);

        // Update field names with new index
        this.updateFieldIndexes(clone, newIndex);

        // Insert after last item
        lastItem.parentNode.insertBefore(clone, lastItem.nextSibling);

        // Update button visibility
        this.updateButtonVisibility();
    }

    /**
     * Remove repeater item
     */
    removeRepeaterItem(target) {
        const items = this.getRepeaterItems();
        if (items.length <= 1) return;

        const itemToRemove = target.closest(this.repeaterSelector);
        if (!itemToRemove) return;

        itemToRemove.remove();

        // Reindex all items
        this.reindexItems();

        // Update button visibility
        this.updateButtonVisibility();
    }

    /**
     * Get all repeater items
     */
    getRepeaterItems() {
        return Array.from(this.element.querySelectorAll(this.repeaterSelector));
    }

    /**
     * Clear all input values in an element
     */
    clearInputs(element) {
        element.querySelectorAll('input, textarea, select').forEach(input => {
            if (input.type === 'checkbox' || input.type === 'radio') {
                input.checked = false;
            } else {
                input.value = '';
            }
        });

        // Hide conditional fields
        const dateRangeWrapper = element.querySelector('.date-range-wrapper');
        if (dateRangeWrapper) {
            dateRangeWrapper.style.display = 'none';
            dateRangeWrapper.querySelectorAll('input').forEach(input => {
                input.removeAttribute('required');
            });
        }
    }

    /**
     * Update field name indexes
     */
    updateFieldIndexes(element, newIndex) {
        element.querySelectorAll('[name]').forEach(input => {
            const name = input.getAttribute('name');
            input.setAttribute('name', name.replace(/\[\d+\]/, `[${newIndex}]`));
        });

        // Update repeater index display
        const indexLabel = element.querySelector('.repeater-index');
        if (indexLabel) {
            indexLabel.textContent = newIndex + 1;
        }
    }

    /**
     * Reindex all items after removal
     */
    reindexItems() {
        const items = this.getRepeaterItems();
        
        items.forEach((item, index) => {
            this.updateFieldIndexes(item, index);
        });
    }

    /**
     * Update add/remove button visibility
     */
    updateButtonVisibility() {
        const items = this.getRepeaterItems();
        
        items.forEach((item, index) => {
            const addBtn = item.querySelector(this.addButtonSelector);
            const removeBtn = item.querySelector(this.removeButtonSelector);
            
            if (index === items.length - 1) {
                // Last item: show both buttons
                if (addBtn) addBtn.style.display = 'flex';
                if (removeBtn) removeBtn.style.display = items.length > 1 ? 'flex' : 'none';
            } else {
                // Other items: hide add, show remove
                if (addBtn) addBtn.style.display = 'none';
                if (removeBtn) removeBtn.style.display = 'flex';
            }
        });
    }

    /**
     * Handle form submission
     */
    async handleSubmit(e) {
        e.preventDefault();

        const submitBtn = this.$('input[type="submit"]');
        if (!submitBtn) return;

        const originalText = submitBtn.value;
        submitBtn.value = 'Submitting...';
        submitBtn.disabled = true;

        try {
            const formData = new FormData(this.element);
            const response = await apiClient.postFormData(formData);

            if (response.success) {
                this.showSuccessModal(
                    response.data?.message,
                    response.data?.redirect_url
                );
            } else {
                alert(response.data?.message || response.data || 'Submission failed. Please try again.');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert('An error occurred. Please try again.');
        } finally {
            submitBtn.value = originalText;
            submitBtn.disabled = false;
        }
    }

    /**
     * Show the success modal and auto-redirect after a short delay.
     * Falls back to a plain alert + immediate redirect if the modal is not
     * present in the current page (i.e. FormRepeater used on other pages).
     *
     * @param {string}      message     Success message from the server.
     * @param {string|null} redirectUrl URL to redirect to after the modal closes.
     */
    showSuccessModal(message, redirectUrl) {
        const modal   = document.getElementById('request_success_modal');
        const msgEl   = document.getElementById('request_success_modal_message');
        const btnEl   = document.getElementById('request_success_modal_btn');

        if (!modal) {
            // Fallback for pages that don't have the modal HTML.
            if (message) alert(message);
            if (redirectUrl) window.location.href = redirectUrl;
            return;
        }

        if (redirectUrl && btnEl) btnEl.href = redirectUrl;

        // Fade in — same pattern used by other modals in the theme.
        modal.style.display = 'block';
        requestAnimationFrame(() => { modal.style.opacity = 1; });

        const closeModal = () => {
            modal.style.opacity = 0;
            setTimeout(() => { modal.style.display = 'none'; }, 200);
        };

        // Close on .close_modal button or overlay click — same pattern as other modals.
        const handleClose = (e) => {
            if (!e.target.closest('#request_success_modal')) return;
            if (e.target.closest('.close_modal') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                closeModal();
                document.removeEventListener('click', handleClose);
            }
        };
        document.addEventListener('click', handleClose);

        // Auto-redirect after 3 seconds.
        if (redirectUrl) {
            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 3000);
        }
    }
}

